import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import { SecurityKeyEnrollmentDetails } from '../../../types';
import { create, parseCreationOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill';
import {
  confirmEnrollResponse,
  confirmSecurityKeyEnrollment,
} from '../../../actions/mfa-enrollment-actions';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { PublicKeyCredentialWithAttestationJSON } from '@github/webauthn-json';
import { captureErrorMessage } from '../../../utilities/analytics/error-reporting';
import {
  isWebAuthnSupported,
  translateRegistrationError,
  WebAuthnRegistrationErrorCode,
} from '../../../helpers/webauthn';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

const X_CSRF_TOKEN_HEADER = 'X-CSRF-TOKEN';

type InitializeProps = {
  deviceName: string;
  csrfToken: string;
  token: string;
};

class EnrollError extends Error {
  public type: string;
  constructor(message: string, type: EnrollErrorType) {
    super(message);
    this.type = type;
  }
}

export enum EnrollApiErrorType {
  deviceNameLengthError = 'deviceNameLengthError',
  rateLimitedError = 'rateLimited',
  enrollFailure = 'enrollFailure',
}

type SecurityKeyConfirmEnrollmentResponse = {
  redirectUrl: string;
};

type SecurityKeyEnrollmentErrorResponse = {
  type: string;
};

export type EnrollErrorType = EnrollApiErrorType | WebAuthnRegistrationErrorCode;

const initialize = async ({
  deviceName,
  csrfToken,
  token,
}: InitializeProps): Promise<SecurityKeyEnrollmentDetails> => {
  try {
    const response = await fetch(
      `/manage/rest/two-step-verification/start-security-key?token=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [X_CSRF_TOKEN_HEADER]: csrfToken,
        },
        body: JSON.stringify({ deviceName }),
      }
    );
    switch (response.status) {
      case 200:
        return response.json() as Promise<SecurityKeyEnrollmentDetails>;
      case 400:
        const errorResponse = (await response.json()) as SecurityKeyEnrollmentErrorResponse;
        const errorType =
          errorResponse.type === 'invalid-device-name'
            ? EnrollApiErrorType.deviceNameLengthError
            : EnrollApiErrorType.enrollFailure;
        throw new EnrollError(
          `/manage/rest/two-step-verification/confirm-security key retruns unexpected status code ${response.status}`,
          errorType
        );
      case 429:
        throw new EnrollError(
          `/manage/rest/two-step-verification/start-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.rateLimitedError
        );
      default:
        throw new EnrollError(
          `/manage/rest/two-step-verification/start-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.enrollFailure
        );
    }
  } catch (error) {
    if (error instanceof EnrollError) {
      throw error;
    }
    throw new EnrollError(error.message, EnrollApiErrorType.enrollFailure);
  }
};

const createCredentialsOnAuthenticator = async (
  securityKeyEnrollmentDetails: SecurityKeyEnrollmentDetails
): Promise<PublicKeyCredentialWithAttestationJSON> => {
  try {
    const options = parseCreationOptionsFromJSON({
      publicKey: { ...securityKeyEnrollmentDetails.creationOptions, timeout: 60000 },
    });
    try {
      const securityKeyCredential = await create(options);
      return securityKeyCredential.toJSON();
    } catch (error) {
      captureErrorMessage('Security key credential creation failed', {
        webauthnError: { name: error.name, message: JSON.stringify(error.message) },
      });
      const errorCode = translateRegistrationError({ error: error, options });
      throw new EnrollError(
        `Failed to create credentials on authenticator: ${error.message}`,
        errorCode
      );
    }
  } catch (error) {
    if (error instanceof EnrollError) {
      throw error;
    }
    captureErrorMessage('Failed to parse creation options for security key', {
      webauthnError: { name: error.name, message: JSON.stringify(error.message) },
    });
    throw new EnrollError(
      `Failed to parse creation options: ${error.message}`,
      WebAuthnRegistrationErrorCode.InvalidCreationOptions
    );
  }
};

type ConfirmProps = PublicKeyCredentialWithAttestationJSON & {
  transactionToken: string;
  csrfToken: string;
  token: string;
  continueUrl?: string;
};

const confirm = async ({
  transactionToken,
  csrfToken,
  token,
  continueUrl,
  ...attestationJson
}: ConfirmProps) => {
  const clientExtensionResultsJsonString = JSON.stringify(attestationJson.clientExtensionResults);

  try {
    const confirmUrl = `/manage/rest/two-step-verification/confirm-security-key?${queryString.stringify(
      {
        transactionToken,
        token,
        continue: continueUrl,
      }
    )}`;

    const response = await fetch(confirmUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [X_CSRF_TOKEN_HEADER]: csrfToken,
      },
      body: JSON.stringify({
        ...attestationJson,
        clientExtensionResults: clientExtensionResultsJsonString,
      }),
    });

    switch (response.status) {
      case 200:
        const { redirectUrl } = (await response.json()) as SecurityKeyConfirmEnrollmentResponse;
        return redirectUrl;
      case 429:
        throw new EnrollError(
          `/manage/rest/two-step-verification/confirm-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.rateLimitedError
        );
      default:
        throw new EnrollError(
          `/manage/rest/two-step-verification/confirm-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.enrollFailure
        );
    }
  } catch (error) {
    if (error instanceof EnrollError) {
      throw error;
    }
    throw new EnrollError(error.message, EnrollApiErrorType.enrollFailure);
  }
};

export const useEnrollSecurityKey = (
  onSuccess: () => void,
  onError: (error: EnrollErrorType) => void
) => {
  const csrfToken = useAppSelector(state => state.csrfToken);
  const token = useAppSelector(state => state.mfaEnrollment.token) || '';
  const isSupported = isWebAuthnSupported();
  const dispatch = useAppDispatch();
  const analyticsClient = useAnalyticsClient();
  const location = useLocation();
  const search = queryString.parse(location.search);
  const [confirmingEnrollment, setConfirmingEnrollment] = useState(false);

  const enrollSecurityKey = useCallback(
    async (deviceName: string) => {
      analyticsClient.trackingEvent({
        page: '2step/enrollment/2-register-security-key',
        subject: '2svEnrolRegisterSecurityKey',
        action: 'started',
      });
      if (!isSupported) {
        analyticsClient.trackingEvent({
          page: '2step/enrollment/2-register-security-key',
          action: 'failed',
          subject: '2svEnrolRegisterSecurityKey',
          attributes: { reason: WebAuthnRegistrationErrorCode.WebAuthnNotSupported },
        });
        return onError(WebAuthnRegistrationErrorCode.WebAuthnNotSupported);
      }

      try {
        setConfirmingEnrollment(true);
        const securityKeyEnrollmentDetails = await initialize({ deviceName, csrfToken, token });

        dispatch(confirmSecurityKeyEnrollment(securityKeyEnrollmentDetails.recoveryCode));

        const securityKeyCredentialJson = await createCredentialsOnAuthenticator(
          securityKeyEnrollmentDetails
        );

        const redirectUrl = await confirm({
          transactionToken: securityKeyEnrollmentDetails.transactionToken,
          csrfToken,
          token,
          continueUrl: search.continue,
          ...securityKeyCredentialJson,
        });
        dispatch(confirmEnrollResponse(redirectUrl));

        analyticsClient.trackingEvent({
          page: '2step/enrollment/2-register-security-key',
          subject: '2svEnrolRegisterSecurityKey',
          action: 'succeeded',
        });

        return onSuccess();
      } catch (e) {
        setConfirmingEnrollment(false);
        analyticsClient.trackingEvent({
          page: '2step/enrollment/2-register-security-key',
          subject: '2svEnrolRegisterSecurityKey',
          action: 'failed',
          attributes: { reason: e.type || EnrollApiErrorType.enrollFailure },
        });
        return onError(e.type || EnrollApiErrorType.enrollFailure);
      }
    },
    [isSupported, analyticsClient, onError, dispatch, onSuccess, csrfToken, search.continue, token]
  );
  return { enrollSecurityKey, confirmingEnrollment };
};
